import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useModalA11y from "@/lib/useModalA11y";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Video, MapPin, X, PlugZap, Unplug, RotateCw,
  Shield, CircleSlash, CalendarPlus, ExternalLink, Download, Copy, RefreshCw, Building2,
  ShieldCheck, ShieldAlert, Ban, CheckCircle2, Clock, MessageSquare, User, Mail, Phone, Calendar as CalIcon,
} from "lucide-react";
import { Skeleton } from "@/components/Skeleton";
import MonthStrip from "@/components/MonthStrip";

// ---- Time helpers — everything renders in Europe/London wall-clock --------
const LZ = "Europe/London";
const dkFmt = new Intl.DateTimeFormat("en-CA", { timeZone: LZ, year: "numeric", month: "2-digit", day: "2-digit" });
const hmFmt = new Intl.DateTimeFormat("en-GB", { timeZone: LZ, hour: "2-digit", minute: "2-digit", hour12: false });
const dayLabelFmt = new Intl.DateTimeFormat("en-GB", { timeZone: LZ, weekday: "short", day: "numeric" });

function londonDateKey(iso) { return dkFmt.format(new Date(iso)); }
function londonHM(iso) {
  const p = hmFmt.formatToParts(new Date(iso));
  const h = Number(p.find((x) => x.type === "hour").value);
  const m = Number(p.find((x) => x.type === "minute").value);
  return { h, m, str: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` };
}
// Convert a London wall-clock (dateKey + HH:MM) into a correct ISO/UTC string.
// London offset is only 0 or +1, so a single correction pass is exact.
function londonWallToISO(dateKey, hour, minute = 0) {
  const [y, mo, d] = dateKey.split("-").map(Number);
  let guess = Date.UTC(y, mo - 1, d, hour, minute, 0);
  const shown = Number(new Intl.DateTimeFormat("en-GB", { timeZone: LZ, hour: "2-digit", hour12: false })
    .formatToParts(new Date(guess)).find((x) => x.type === "hour").value);
  guess += (hour - shown) * 3600000;
  return new Date(guess).toISOString();
}

const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 07:00 – 20:00
const SERVICES = [
  "Initial sport therapy assessment",
  "Follow-up sport therapy",
  "Sports massage",
  "Strength & conditioning",
  "Return-to-sport testing",
];
const DURATIONS = [30, 45, 60, 90];

// Local (browser) Monday@noon anchor for the visible week. Noon avoids any
// midnight/DST rollover when we derive each column's London date key.
function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon = 0
  x.setHours(12, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
}

// ---- Clinician colour map (resolved to real user IDs at runtime) ----------
const NAME_COLOURS = {
  andrea: { hex: "#f47a20", label: "Orange" },
  saimon: { hex: "#3B82F6", label: "Blue" },
  luca: { hex: "#22C55E", label: "Green" },
  francesco: { hex: "#A855F7", label: "Purple" },
};
const FALLBACK = ["#EC4899", "#14B8A6", "#EAB308", "#8B5CF6", "#06B6D4", "#F97316"];
const BUSY_HEX = "#9CA3AF"; // neutral grey for external Google busy blocks
const ORG_HEX = "#6366F1"; // distinct indigo for organisation Google-only events
const firstName = (n) => (n || "").trim().split(" ")[0].toLowerCase();

export default function ClinicianCalendar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [team, setTeam] = useState([]);        // [{id,name,role}]
  const [appts, setAppts] = useState([]);       // merged FL appointments (all team)
  const [gEvents, setGEvents] = useState([]);   // logged-in clinician's Google busy
  const [orgEvents, setOrgEvents] = useState([]); // organisation Google-only events
  const [patients, setPatients] = useState([]);
  const [conn, setConn] = useState(null);
  const [calToken, setCalToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [calView, setCalView] = useState("month");
  const [selectedDay, setSelectedDay] = useState(() => dkFmt.format(new Date()));
  const [stripMonth, setStripMonth] = useState(() => dkFmt.format(new Date()).slice(0, 7) + "-01");
  const [connBusy, setConnBusy] = useState(false);
  const [showSub, setShowSub] = useState(false);
  const subRef = useModalA11y({ open: showSub && !!calToken, onClose: () => setShowSub(false) });

  // Source toggles
  const [mineOn, setMineOn] = useState(true);
  const [googleOn, setGoogleOn] = useState(true);
  const [orgOn, setOrgOn] = useState(true);
  const [teamOn, setTeamOn] = useState({}); // {clinicianId: bool}

  // Modals
  const [detail, setDetail] = useState(null); // {kind:'appt'|'busy', ...}
  const detailRef = useModalA11y({ open: !!detail, onClose: () => setDetail(null) });
  const [createSlot, setCreateSlot] = useState(null); // {dateKey, hour, minute}

  const googleConnected = !!conn?.providers?.google?.connected;
  const googleAvailable = !!conn?.providers?.google?.available;
  const orgConnected = !!conn?.organisation?.connected;
  const orgCanManage = !!conn?.organisation?.can_manage;
  const orgAvailable = !!conn?.organisation?.available;

  // Colour resolved to real user IDs (name match → central map, else palette).
  const colourById = useMemo(() => {
    const map = {};
    team.forEach((c, i) => {
      const hit = NAME_COLOURS[firstName(c.name)];
      map[c.id] = hit?.hex || FALLBACK[i % FALLBACK.length];
    });
    return map;
  }, [team]);

  const teamOthers = useMemo(() => team.filter((c) => c.id !== user?.id), [team, user]);

  const refreshConn = () => api.get("/calendar/status").then((r) => setConn(r.data)).catch(() => setConn(null));

  const loadAppts = async (list) => {
    const roster = list || team;
    if (!roster.length) return;
    const results = await Promise.all(
      roster.map((c) => api.get(`/appointments/by-clinician/${c.id}`).then((r) => r.data).catch(() => [])),
    );
    // Flatten + de-dupe by appointment id (a clinician appears once).
    const seen = new Set();
    const merged = [];
    results.flat().forEach((a) => { if (a?.id && !seen.has(a.id)) { seen.add(a.id); merged.push(a); } });
    setAppts(merged);
  };

  // Initial load
  useEffect(() => {
    (async () => {
      setLoading(true);
      const [clins] = await Promise.all([
        api.get("/public/clinicians").then((r) => r.data).catch(() => []),
        refreshConn(),
      ]);
      setTeam(clins);
      // Default = My Schedule: my FL appts + my Google busy, team off.
      const initTeam = {};
      clins.forEach((c) => { if (c.id !== user?.id) initTeam[c.id] = false; });
      setTeamOn(initTeam);
      api.get("/calendar/token").then((r) => setCalToken(r.data)).catch(() => {});
      api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
      await loadAppts(clins);
      setLoading(false);
    })();
    const c = params.get("connected");
    if (c === "google") toast.success("Connected to Google Calendar");
    const err = params.get("connect_error");
    if (err) toast.error(`Could not connect Google Calendar: ${params.get("reason") || "unknown error"}`);
    // eslint-disable-next-line
  }, []);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i);
    return { date: d, key: londonDateKey(d), label: dayLabelFmt.format(d) };
  }), [weekStart]);

  const [reloadKey, setReloadKey] = useState(0);

  // External (Google) busy windows for the visible week — clinician personal + org.
  useEffect(() => {
    const from = new Date(weekDays[0].date); from.setHours(0, 0, 0, 0);
    const to = new Date(weekDays[6].date); to.setHours(23, 59, 59, 0);
    const params = { from: from.toISOString(), to: to.toISOString() };
    if (googleConnected) {
      api.get("/calendar/google/events", { params }).then((r) => setGEvents(r.data || [])).catch(() => setGEvents([]));
    } else { setGEvents([]); }
    if (orgConnected) {
      api.get("/calendar/organisation/google/events", { params }).then((r) => setOrgEvents(r.data || [])).catch(() => setOrgEvents([]));
    } else { setOrgEvents([]); }
  }, [googleConnected, orgConnected, weekStart, reloadKey]); // eslint-disable-line

  // Visible items placed by London date+hour.
  const items = useMemo(() => {
    const out = [];
    appts.forEach((a) => {
      if (!a.scheduled_at || a.status === "cancelled" || a.status === "declined") return;
      const mine = a.clinician_id === user?.id;
      const visible = mine ? mineOn : !!teamOn[a.clinician_id];
      if (!visible) return;
      out.push({ kind: "appt", id: a.id, raw: a, mine,
        colour: colourById[a.clinician_id] || BUSY_HEX,
        dateKey: londonDateKey(a.scheduled_at), hm: londonHM(a.scheduled_at) });
    });
    const clinEventIds = new Set(appts.map((a) => a.google_event_id).filter(Boolean));
    const orgEventIds = new Set(appts.map((a) => a.organisation_google_event_id).filter(Boolean));
    if (googleOn && googleConnected) {
      gEvents.forEach((e) => {
        if (!e.start || (e.id && clinEventIds.has(e.id))) return; // suppress FL-origin copies
        out.push({ kind: "busy", id: e.id, raw: e,
          dateKey: londonDateKey(e.start), hm: londonHM(e.start),
          endStr: e.end ? londonHM(e.end).str : null });
      });
    }
    if (orgOn && orgConnected) {
      orgEvents.forEach((e) => {
        if (!e.start || (e.id && orgEventIds.has(e.id))) return; // suppress FL-mirrored copies
        out.push({ kind: "org", id: e.id, raw: e,
          dateKey: londonDateKey(e.start), hm: londonHM(e.start),
          endStr: e.end ? londonHM(e.end).str : null });
      });
    }
    return out;
  }, [appts, gEvents, orgEvents, mineOn, googleOn, orgOn, teamOn, googleConnected, orgConnected, colourById, user]);

  const cellItems = (dayKey, hour) => items.filter((it) => it.dateKey === dayKey && it.hm.h === hour);

  // Month-strip markers + selected-day agenda (reuse the same `items` filtering
  // so quick-views / source toggles / clinician colours all stay consistent).
  const dayMetaMap = useMemo(() => {
    const m = new Map();
    items.forEach((it) => {
      if (it.kind !== "appt") return;
      const e = m.get(it.dateKey) || { count: 0, colours: new Set() };
      e.count += 1; e.colours.add(it.colour); m.set(it.dateKey, e);
    });
    return m;
  }, [items]);
  const dayMeta = (isoDay) => {
    const e = dayMetaMap.get(isoDay);
    return e ? { count: e.count, dots: [...e.colours] } : null;
  };
  const dayItems = useMemo(
    () => items.filter((it) => it.dateKey === selectedDay)
      .sort((a, b) => (a.hm.h - b.hm.h) || (a.hm.m - b.hm.m)),
    [items, selectedDay],
  );
  const selectDay = (isoDay) => {
    setSelectedDay(isoDay);
    setWeekStart(startOfWeek(new Date(`${isoDay}T12:00:00`)));
  };
  const goTodayMonth = () => {
    const tk = dkFmt.format(new Date());
    setSelectedDay(tk);
    setStripMonth(tk.slice(0, 7) + "-01");
    setWeekStart(startOfWeek(new Date()));
  };
  // When the strip's month changes, move the selected day into that month so
  // the agenda below always matches the visible strip (today if it falls in
  // the new month, otherwise the 1st).
  const changeStripMonth = (firstIso) => {
    setStripMonth(firstIso);
    const tk = dkFmt.format(new Date());
    selectDay(tk.slice(0, 7) === firstIso.slice(0, 7) ? tk : firstIso);
  };
  const selectedDayLabel = new Date(`${selectedDay}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  const stripMonthLabel = new Date(`${stripMonth}T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  const shift = (n) => { const nx = new Date(weekStart); nx.setDate(nx.getDate() + n * 7); setWeekStart(nx); };

  // Manual "Sync calendars" — refreshes FL appts, connection status and the
  // external Google windows. Never recreates Google events.
  const syncAll = async () => {
    setSyncing(true);
    try {
      await Promise.all([loadAppts(), refreshConn(), new Promise((r) => setTimeout(r, 300))]);
      setReloadKey((k) => k + 1);
      toast.success("Calendars updated");
    } catch { toast.error("Could not refresh — try again"); }
    finally { setSyncing(false); }
  };

  // Quick-view presets
  const applyQuickView = (view) => {
    if (view === "mine") { setMineOn(true); setGoogleOn(true); setOrgOn(false); setTeamOn((s) => mapAll(s, false)); }
    else if (view === "team") { setMineOn(false); setGoogleOn(false); setOrgOn(true); setTeamOn((s) => mapAll(s, true)); }
    else { setMineOn(true); setGoogleOn(true); setOrgOn(true); setTeamOn((s) => mapAll(s, true)); }
  };
  const mapAll = (obj, val) => { const n = {}; Object.keys(obj).forEach((k) => { n[k] = val; }); return n; };

  const connect = async () => {
    setConnBusy(true);
    try {
      const { data } = await api.get(`/calendar/google/connect`);
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Google sync is not configured yet.");
      setConnBusy(false);
    }
  };
  const connectOrg = async () => {
    setConnBusy(true);
    try {
      const { data } = await api.get(`/calendar/organisation/google/connect`);
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Organisation Google sync is not configured yet.");
      setConnBusy(false);
    }
  };
  const disconnectOrg = async () => {
    setConnBusy(true);
    try { await api.post(`/calendar/organisation/google/disconnect`); toast.success("Functional LAB Google disconnected"); await refreshConn(); setOrgEvents([]); }
    catch { toast.error("Disconnect failed"); }
    finally { setConnBusy(false); }
  };
  const disconnect = async () => {
    setConnBusy(true);
    try { await api.post(`/calendar/google/disconnect`); toast.success("Google disconnected"); await refreshConn(); setGEvents([]); }
    catch { toast.error("Disconnect failed"); }
    finally { setConnBusy(false); }
  };
  const syncNow = async () => {
    setConnBusy(true);
    try {
      const { data } = await api.post(`/calendar/google/sync`);
      toast.success(`Google synced — ${data.count} event${data.count === 1 ? "" : "s"}`);
      await refreshConn();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed — try reconnecting"); }
    finally { setConnBusy(false); }
  };

  const rangeLabel = `${weekDays[0].date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${weekDays[6].date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
  const todayKey = londonDateKey(new Date());

  const copyCalUrl = () => {
    navigator.clipboard?.writeText(window.location.origin + (calToken?.path || ""));
    toast.success("Copied — paste into Google Calendar → Add by URL");
  };
  const rotateCal = async () => {
    if (!window.confirm("Regenerate the subscription URL? Any calendar app subscribed to the old one will stop syncing.")) return;
    const { data } = await api.post("/calendar/token/rotate");
    setCalToken(data); toast.success("Subscription URL rotated");
  };

  return (
    <DashLayout>
      <div className="mb-6">
        <div className="fl-label text-[#f47a20]">{t("pages.cCalendar.eyebrow")}</div>
        <div className="flex items-end justify-between mt-2 flex-wrap gap-3">
          <h1 className="fl-heading text-3xl">{calView === "month" ? stripMonthLabel : rangeLabel}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 rounded-full p-1" style={{ background: "var(--fl-panel-2, rgba(255,255,255,0.04))" }}>
              {["month", "week"].map((v) => (
                <button key={v} onClick={() => setCalView(v)} data-testid={`cal-view-${v}`}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
                        style={{ background: calView === v ? "#3B82F6" : "transparent", color: calView === v ? "#fff" : "var(--fl-muted)" }}>
                  {v === "month" ? "Month" : "Week"}
                </button>
              ))}
            </div>
            {["mine", "team", "all"].map((v) => (
              <button key={v} onClick={() => applyQuickView(v)} className="fl-btn-ghost text-xs px-3 py-2"
                      data-testid={`cal-quickview-${v}`}>
                {v === "mine" ? "My schedule" : v === "team" ? "Team" : "All"}
              </button>
            ))}
            {calView === "week" ? (
              <>
                <button onClick={() => shift(-1)} className="fl-btn-ghost px-3 py-2" data-testid="cal-prev"><ChevronLeft size={16} /></button>
                <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="fl-btn-ghost px-3 py-2" data-testid="cal-today">Today</button>
                <button onClick={() => shift(1)} className="fl-btn-ghost px-3 py-2" data-testid="cal-next"><ChevronRight size={16} /></button>
              </>
            ) : (
              <button onClick={goTodayMonth} className="fl-btn-ghost px-3 py-2" data-testid="cal-today">Today</button>
            )}
            <button onClick={syncAll} disabled={syncing} className="fl-btn text-xs px-3 py-2" data-testid="cal-sync-all">
              <RotateCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync calendars"}
            </button>
            <button onClick={() => setShowSub(true)} className="fl-btn-ghost text-xs px-3 py-2" data-testid="cal-subscribe-btn">
              <Download size={14} /> iCal feed
            </button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-6 min-w-0">
        {/* ---------------- Source sidebar ---------------- */}
        <aside className="space-y-5 min-w-0" data-testid="cal-source-sidebar">
          {/* My calendars */}
          <div className="fl-card p-4">
            <div className="fl-label mb-3">My calendars</div>
            <SourceRow
              testid="src-my-fl" label="My Functional LAB appointments"
              colour={colourById[user?.id] || "#f47a20"} on={mineOn} onToggle={() => setMineOn((v) => !v)}
            />
            <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--fl-border)" }}>
              <SourceRow
                testid="src-google" label="Personal Google Calendar"
                colour={BUSY_HEX} on={googleOn && googleConnected}
                disabled={!googleConnected}
                onToggle={() => setGoogleOn((v) => !v)}
                sub={googleConnected
                  ? `Connected · ${conn?.providers?.google?.info?.external_email || "busy shown as grey"}`
                  : (googleAvailable ? "Not connected" : "Coming soon")}
              />
              <div className="flex items-center gap-2 mt-2">
                {googleConnected ? (
                  <>
                    <button onClick={syncNow} disabled={connBusy} className="fl-btn text-[11px]" data-testid="cal-sync-now">
                      <RotateCw size={11} className={connBusy ? "animate-spin" : ""} /> Sync
                    </button>
                    <button onClick={disconnect} disabled={connBusy} className="fl-btn-ghost text-[11px]" data-testid="cal-disconnect">
                      <Unplug size={11} /> Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => googleAvailable ? connect() : toast.info("Google sync unlocks once credentials are added.")}
                    disabled={connBusy}
                    className={`text-[11px] ${googleAvailable ? "fl-btn" : "fl-btn-ghost opacity-70"}`}
                    data-testid="cal-connect-google"
                  >
                    <PlugZap size={11} /> {googleAvailable ? "Connect Google" : "Coming soon"}
                  </button>
                )}
              </div>
              {googleConnected && (
                <div className="mt-2 flex items-start gap-1.5 text-[10px]" style={{ color: "var(--fl-muted-2)" }}>
                  <Shield size={11} className="text-emerald-400 shrink-0 mt-0.5" />
                  Only busy times are shown here — never your personal event titles or details.
                </div>
              )}
            </div>
          </div>

          {/* Functional LAB team */}
          <div className="fl-card p-4">
            <div className="fl-label mb-3">Functional LAB</div>
            {/* Organisation Google calendar */}
            <div className="pb-3 mb-3 border-b" style={{ borderColor: "var(--fl-border)" }}>
              <SourceRow
                testid="src-org-google" label="Functional LAB Google Calendar"
                colour={ORG_HEX} on={orgOn && orgConnected}
                disabled={!orgConnected}
                onToggle={() => setOrgOn((v) => !v)}
                sub={orgConnected
                  ? `Connected · ${conn?.organisation?.info?.external_email || "central calendar"}`
                  : (orgAvailable ? "Not connected" : "Coming soon")}
              />
              {orgCanManage && (
                <div className="flex items-center gap-2 mt-2">
                  {orgConnected ? (
                    <button onClick={disconnectOrg} disabled={connBusy} className="fl-btn-ghost text-[11px]" data-testid="cal-org-disconnect">
                      <Unplug size={11} /> Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={() => orgAvailable ? connectOrg() : toast.info("Google sync unlocks once credentials are added.")}
                      disabled={connBusy}
                      className={`text-[11px] ${orgAvailable ? "fl-btn" : "fl-btn-ghost opacity-70"}`}
                      data-testid="cal-connect-org"
                    >
                      <Building2 size={11} /> {orgAvailable ? "Connect Functional LAB Google" : "Coming soon"}
                    </button>
                  )}
                </div>
              )}
              {!orgCanManage && !orgConnected && (
                <div className="mt-1 text-[10px]" style={{ color: "var(--fl-muted-2)" }}>Connected by an admin.</div>
              )}
            </div>
            <div className="fl-label mb-2">Team</div>
            <div className="space-y-2">
              {teamOthers.map((c) => (
                <SourceRow
                  key={c.id} testid={`src-team-${c.id}`}
                  label={c.name} colour={colourById[c.id]}
                  on={!!teamOn[c.id]}
                  onToggle={() => setTeamOn((s) => ({ ...s, [c.id]: !s[c.id] }))}
                />
              ))}
              {teamOthers.length === 0 && <div className="text-xs text-zinc-500">No other clinicians.</div>}
            </div>
          </div>

          {/* Legend */}
          <div className="fl-card p-4">
            <div className="fl-label mb-2">Legend</div>
            <div className="space-y-1.5 text-xs" style={{ color: "var(--fl-muted)" }}>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: "#f47a20" }} /> Functional LAB appointment</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: BUSY_HEX }} /> Personal Google busy (read-only)</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: ORG_HEX }} /> Functional LAB Google (read-only)</div>
            </div>
          </div>
        </aside>

        {/* ---------------- Week grid ---------------- */}
        <div className="min-w-0">
          {loading ? (
            <div className="fl-card p-4 space-y-3" data-testid="cal-loading">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-md" />)}
            </div>
          ) : calView === "month" ? (
            <div className="space-y-4" data-testid="cal-month-view">
              <MonthStrip monthAnchor={stripMonth} onMonthChange={changeStripMonth} value={selectedDay} onChange={selectDay} getDayMeta={dayMeta} accent="#3B82F6" testid="cal-month-strip" />
              <div className="fl-card p-4" data-testid="cal-day-agenda">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="fl-heading text-lg">{selectedDayLabel}</h2>
                  <span className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{dayItems.length} item{dayItems.length === 1 ? "" : "s"}</span>
                </div>
                {dayItems.length === 0 ? (
                  <div className="text-sm py-6 text-center" style={{ color: "var(--fl-muted-2)" }} data-testid="cal-day-empty">
                    Nothing scheduled. Switch to Week to tap a free slot and book.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dayItems.map((it) => it.kind === "appt" ? (
                      <button key={it.id} onClick={() => setDetail(it)}
                              className="w-full text-left fl-card p-3 flex items-center gap-3 hover:brightness-110 transition"
                              style={{ borderLeft: `3px solid ${it.colour}` }}
                              data-testid={`cal-agenda-appt-${it.id}`}>
                        <div className="text-sm font-semibold tabular-nums" style={{ color: it.colour, minWidth: "48px" }}>{it.hm.str}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate" style={{ color: "var(--fl-text)" }}>{it.raw.patient_name || "Patient"}</div>
                          <div className="text-xs text-zinc-500 truncate">{it.raw.service} · {it.raw.clinician_name?.split(" ")[0]}</div>
                        </div>
                        {it.raw.mode === "remote" ? <Video size={15} className="text-zinc-400 shrink-0" /> : <MapPin size={15} className="text-zinc-400 shrink-0" />}
                      </button>
                    ) : (
                      <button key={it.id || `${it.dateKey}-${it.hm.str}`} onClick={() => setDetail(it)}
                              className="w-full text-left fl-card p-3 flex items-center gap-3 hover:brightness-110 transition"
                              style={{ borderLeft: `3px solid ${it.kind === "org" ? ORG_HEX : BUSY_HEX}` }}
                              data-testid={it.kind === "org" ? "cal-agenda-org" : "cal-agenda-busy"}>
                        <div className="text-sm font-semibold tabular-nums" style={{ color: it.kind === "org" ? ORG_HEX : BUSY_HEX, minWidth: "48px" }}>{it.hm.str}</div>
                        <div className="flex-1 min-w-0 flex items-center gap-1.5" style={{ color: it.kind === "org" ? ORG_HEX : BUSY_HEX }}>
                          {it.kind === "org" ? <Building2 size={13} /> : <CircleSlash size={13} />}
                          <span className="text-sm font-medium">{it.kind === "org" ? "FL — Busy" : "Busy"}</span>
                          {it.endStr && <span className="text-xs text-zinc-500">· until {it.endStr}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="fl-card overflow-x-auto">
              <div className="grid grid-cols-8 min-w-[880px]">
                <div className="border-r border-white/10 border-b p-2" />
                {weekDays.map((d) => {
                  const isToday = d.key === todayKey;
                  const count = items.filter((it) => it.dateKey === d.key && it.kind === "appt").length;
                  return (
                    <div key={d.key} className={`border-r border-white/10 border-b p-2 text-center ${isToday ? "bg-[#f47a20]/10" : ""}`}>
                      <div className={`fl-label ${isToday ? "text-[#f47a20]" : ""}`}>{d.label}</div>
                      <div className="text-[10px] text-zinc-500 mt-1">{count} appt</div>
                    </div>
                  );
                })}
                {HOURS.map((h) => (
                  <RowTime key={h} hour={h} weekDays={weekDays}
                           cellItems={cellItems}
                           onEmptyClick={(dateKey) => setCreateSlot({ dateKey, hour: h, minute: 0 })}
                           onItem={(it) => setDetail(it)} />
                ))}
              </div>
            </div>
          )}
          <div className="text-[11px] mt-2 flex items-center gap-1.5" style={{ color: "var(--fl-muted-2)" }}>
            <CalendarPlus size={12} /> Tap any free slot to book a session. All times shown in London (Europe/London).
          </div>
        </div>
      </div>

      {/* ---------------- Detail modal ---------------- */}
      {detail && <DetailModal detail={detail} modalRef={detailRef} nav={nav} close={() => setDetail(null)}
                              patients={patients} me={user}
                              onChanged={async () => { await loadAppts(); setReloadKey((k) => k + 1); }} />}

      {/* ---------------- Create modal ---------------- */}
      {createSlot && (
        <CreateModal
          slot={createSlot}
          patients={patients}
          close={() => setCreateSlot(null)}
          onCreated={async () => { setCreateSlot(null); await loadAppts(); }}
          clinicianId={user?.id}
        />
      )}

      {/* ---------------- iCal subscribe dialog (unchanged feed) ---------------- */}
      {showSub && calToken && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowSub(false)}>
          <div ref={subRef} role="dialog" aria-modal="true" className="fl-card p-6 w-full max-w-xl"
               onClick={(e) => e.stopPropagation()} data-testid="cal-subscribe-dialog">
            <div className="fl-label text-[#f47a20]">Live calendar feed</div>
            <div className="fl-heading text-2xl mt-2">Subscribe from any calendar app</div>
            <p className="text-sm mt-3" style={{ color: "var(--fl-muted)" }}>
              Read-only feed of your Functional LAB appointments. Paste the URL into Google, Outlook or Apple Calendar.
            </p>
            <div className="mt-4 flex gap-2">
              <input className="fl-input font-mono text-xs" readOnly value={`${window.location.origin}${calToken.path}`}
                     data-testid="cal-subscribe-url" onClick={(e) => e.target.select()} />
              <button className="fl-btn" onClick={copyCalUrl} data-testid="cal-copy-url"><Copy size={14} /></button>
            </div>
            <div className="flex justify-between gap-2 mt-6 pt-4 border-t" style={{ borderColor: "var(--fl-border)" }}>
              <button className="fl-btn-ghost text-xs" onClick={rotateCal} data-testid="cal-rotate-btn"><RefreshCw size={12} /> Rotate URL</button>
              <button className="fl-btn" onClick={() => setShowSub(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </DashLayout>
  );
}

// ---------------------------------------------------------------------------
function SourceRow({ testid, label, colour, on, onToggle, disabled, sub }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onToggle}
      disabled={disabled}
      className={`w-full flex items-center gap-2.5 text-left ${disabled ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
      data-testid={testid}
    >
      <span className="w-4 h-4 rounded-[4px] border flex items-center justify-center shrink-0"
            style={{ background: on ? colour : "transparent", borderColor: colour }}>
        {on && <span className="w-1.5 h-1.5 rounded-[1px] bg-white" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-sm block truncate" style={{ color: "var(--fl-text)" }}>{label}</span>
        {sub && <span className="text-[10px] block truncate" style={{ color: "var(--fl-muted-2)" }}>{sub}</span>}
      </span>
    </button>
  );
}

function RowTime({ hour, weekDays, cellItems, onEmptyClick, onItem }) {
  return (
    <>
      <div className="border-r border-white/10 border-b p-2 text-xs text-zinc-500 text-right pr-3">{String(hour).padStart(2, "0")}:00</div>
      {weekDays.map((d) => {
        const list = cellItems(d.key, hour);
        const weekend = [0, 6].includes(d.date.getDay());
        const offHours = hour < 9 || hour >= 18 || weekend;
        return (
          <div
            key={`${d.key}-${hour}`}
            onClick={() => onEmptyClick(d.key)}
            className={`border-r border-white/10 border-b p-1 min-h-[54px] relative cursor-pointer transition-colors hover:bg-white/[0.03] ${offHours ? "bg-black/20" : ""}`}
            data-testid={`cal-cell-${d.key}-${hour}`}
          >
            {list.map((it) => it.kind === "appt" ? (
              <button
                key={it.id}
                onClick={(e) => { e.stopPropagation(); onItem(it); }}
                className="block w-full text-left text-[10px] p-1.5 rounded mb-1 hover:brightness-125"
                style={{ background: `${it.colour}26`, borderLeft: `3px solid ${it.colour}` }}
                data-testid={`cal-appt-${it.id}`}
              >
                <div className="font-semibold text-white truncate">{it.raw.patient_name || "Patient"}</div>
                <div className="text-zinc-400 truncate">{it.raw.service}</div>
                <div className="text-zinc-500">{it.hm.str} · {it.raw.clinician_name?.split(" ")[0]}</div>
              </button>
            ) : (
              <button
                key={it.id || `${it.dateKey}-${it.hm.str}`}
                onClick={(e) => { e.stopPropagation(); onItem(it); }}
                className="block w-full text-left text-[10px] p-1.5 rounded mb-1 hover:brightness-110"
                style={{ background: `${it.kind === "org" ? ORG_HEX : BUSY_HEX}22`, borderLeft: `3px solid ${it.kind === "org" ? ORG_HEX : BUSY_HEX}` }}
                data-testid={it.kind === "org" ? "cal-org-busy" : "cal-busy"}
              >
                <div className="flex items-center gap-1 font-semibold" style={{ color: it.kind === "org" ? ORG_HEX : BUSY_HEX }}>
                  {it.kind === "org" ? <Building2 size={10} /> : <CircleSlash size={10} />} {it.kind === "org" ? "FL — Busy" : "Busy"}
                </div>
                <div className="text-zinc-500">{it.hm.str}{it.endStr ? `–${it.endStr}` : ""}</div>
              </button>
            ))}
          </div>
        );
      })}
    </>
  );
}

const STATUS_META = {
  pending:     { label: "Pending",     bg: "rgba(245,158,11,.15)", fg: "#F59E0B", Icon: Clock },
  confirmed:   { label: "Confirmed",   bg: "rgba(34,197,94,.15)",  fg: "#22C55E", Icon: ShieldCheck },
  declined:    { label: "Declined",    bg: "rgba(239,68,68,.15)",  fg: "#EF4444", Icon: ShieldAlert },
  cancelled:   { label: "Cancelled",   bg: "rgba(239,68,68,.10)",  fg: "#EF4444", Icon: Ban },
  rescheduled: { label: "Rescheduled", bg: "rgba(59,130,246,.15)", fg: "#3B82F6", Icon: CalIcon },
  completed:   { label: "Completed",   bg: "rgba(34,197,94,.12)",  fg: "#22C55E", Icon: CheckCircle2 },
};

function DetailModal({ detail, modalRef, nav, close, patients, me, onChanged }) {
  const isAppt = detail.kind === "appt";
  const isOrg = detail.kind === "org";
  const a = detail.raw;

  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [rDate, setRDate] = useState(isAppt ? londonDateKey(a.scheduled_at) : "");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [picked, setPicked] = useState("");
  const [ctx, setCtx] = useState(null);
  const [ctxLoading, setCtxLoading] = useState(false);

  useEffect(() => {
    if (!isAppt || !a?.id) return;
    setCtxLoading(true); setCtx(null);
    api.get(`/appointments/${a.id}/context`)
      .then((r) => setCtx(r.data))
      .catch(() => setCtx({ source: "denied" }))
      .finally(() => setCtxLoading(false));
  }, [isAppt, a?.id]);

  const status = a?.status || "confirmed";
  const st = STATUS_META[status] || STATUS_META.confirmed;
  const StIcon = st.Icon;
  const patient = isAppt ? (patients || []).find((p) => p.id === a.patient_id) : null;
  const durationMin = a?.duration_min || 60;
  const endStr = isAppt ? londonHM(new Date(new Date(a.scheduled_at).getTime() + durationMin * 60000).toISOString()).str : null;
  const canManage = isAppt && detail.mine && !["cancelled", "declined", "completed"].includes(status);
  const isPending = status === "pending";

  const loadSlots = async (date) => {
    setSlotsLoading(true); setPicked("");
    try {
      const r = await api.get("/appointments/availability", {
        params: { clinician_id: a.clinician_id, date, duration_min: durationMin },
      });
      setSlots(r.data?.slots || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load slots"); setSlots([]);
    } finally { setSlotsLoading(false); }
  };

  const openReschedule = () => {
    setShowReschedule(true); setConfirmCancel(false);
    loadSlots(rDate);
  };

  const patch = async (newStatus, extra = {}) => {
    setSaving(true);
    try {
      const body = { status: newStatus, ...extra };
      if (note.trim()) body.note = note.trim();
      await api.patch(`/appointments/${a.id}/status`, body);
      toast.success(`Marked as ${newStatus}`);
      await onChanged();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the appointment");
    } finally { setSaving(false); }
  };

  const messagePatient = () => {
    const first = (a.patient_name || "there").split(" ")[0];
    nav(`/clinician/messages?to=${encodeURIComponent(a.patient_id)}&prefill=${encodeURIComponent(`Hi ${first}, `)}`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={close}>
      <div ref={modalRef} role="dialog" aria-modal="true"
           className="fl-card fl-card-elevated w-full max-w-lg my-6 max-h-[88vh] flex flex-col"
           onClick={(e) => e.stopPropagation()} data-testid="cal-detail-modal">
        {/* Sticky header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b shrink-0 sticky top-0 z-10"
             style={{ borderColor: "var(--fl-border)", background: "var(--fl-card, #111)" }}>
          <div className="min-w-0">
            <div className="fl-label text-[#f47a20]">{isAppt ? "Appointment" : isOrg ? "Functional LAB Google" : "External busy"}</div>
            <div className="fl-heading text-2xl mt-1 truncate">
              {isAppt ? (a.patient_name || "Patient") : isOrg ? "Functional LAB — Busy" : "Busy — Google Calendar"}
            </div>
            {isAppt && <div className="text-xs text-zinc-500 mt-0.5">{a.service}</div>}
          </div>
          <button onClick={close} aria-label="Close" data-testid="cal-detail-close" className="shrink-0 mt-1"><X size={18} className="text-zinc-400" /></button>
        </div>

        {/* Scrollable body */}
        <div className="p-5 overflow-y-auto">
          {isAppt ? (
            <div className="space-y-4">
              {/* Status */}
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full"
                   style={{ background: st.bg, color: st.fg, border: `1px solid ${st.fg}55` }} data-testid="cal-detail-status">
                <StIcon size={11} /> {st.label}
              </div>

              {/* Patient */}
              <div className="fl-card p-3">
                <div className="fl-label mb-2 flex items-center gap-1.5"><User size={12} /> Patient</div>
                <div className="text-sm font-medium">{a.patient_name || patient?.name || "Patient"}</div>
                {patient?.email && <div className="text-xs text-zinc-500 flex items-center gap-1.5 mt-1"><Mail size={11} /> {patient.email}</div>}
                {patient?.phone && <div className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5"><Phone size={11} /> {patient.phone}</div>}
                {a.patient_id && (
                  <button onClick={() => nav(`/clinician/patient/${a.patient_id}`)}
                          className="fl-btn-ghost text-[11px] mt-2" data-testid="cal-detail-profile">
                    <ExternalLink size={11} /> Open patient profile
                  </button>
                )}
              </div>

              {/* Appointment details */}
              <div className="grid grid-cols-2 gap-3">
                <div className="fl-card p-3">
                  <div className="fl-label mb-1">When (London)</div>
                  <div className="text-sm">{londonDateKey(a.scheduled_at)}</div>
                  <div className="text-sm text-[#f47a20]">{detail.hm.str}{endStr ? `–${endStr}` : ""} · {durationMin} min</div>
                </div>
                <div className="fl-card p-3">
                  <div className="fl-label mb-1">Details</div>
                  <div className="text-sm capitalize flex items-center gap-1.5">
                    {a.mode === "remote" ? <Video size={13} /> : <MapPin size={13} />}{(a.mode || "in_person").replace("_", " ")}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">{a.clinician_name || "—"}</div>
                </div>
              </div>

              {/* Reason for booking / clinical context (clinician-only) */}
              <div className="fl-card p-3" data-testid="cal-detail-reason">
                {ctxLoading ? (
                  <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>Loading context…</div>
                ) : ctx?.source === "booking_reason" ? (
                  <>
                    <div className="fl-label mb-1">Reason for booking · patient-provided</div>
                    <div className="text-sm" style={{ color: "var(--fl-text)" }}>{ctx.booking_reason}</div>
                  </>
                ) : ctx?.source === "clinical_notes" ? (
                  <>
                    <div className="fl-label mb-1">Clinical context from previous notes</div>
                    <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "var(--fl-text)" }}>{ctx.summary}</div>
                    {!ctx.insufficient && (
                      <div className="text-[10px] mt-2" style={{ color: "var(--fl-muted-2)" }}>
                        AI-generated summary from existing Assessments &amp; Notes — verify against source record.
                        {ctx.provenance && <><br/>Summarised from {ctx.provenance.count} Clinical Note{ctx.provenance.count === 1 ? "" : "s"}{ctx.provenance.latest ? ` · latest ${ctx.provenance.latest}` : ""}</>}
                      </div>
                    )}
                    {a.patient_id && (
                      <button onClick={() => nav(`/clinician/patient/${a.patient_id}`)} className="fl-btn-ghost text-[11px] mt-2" data-testid="cal-detail-view-notes">
                        <ExternalLink size={11} /> View Assessments &amp; Notes
                      </button>
                    )}
                  </>
                ) : ctx?.source === "denied" ? (
                  <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>Clinical context is not available for this patient under your permissions.</div>
                ) : (
                  <div className="text-sm" style={{ color: "var(--fl-muted-2)" }}>No new reason provided and no previous Assessments &amp; Notes available.</div>
                )}
              </div>

              {/* Actions — owner only, active statuses */}
              {!detail.mine ? (
                <div className="text-[11px] flex items-start gap-1.5" style={{ color: "var(--fl-muted-2)" }}>
                  <Shield size={12} className="shrink-0 mt-0.5" /> This appointment belongs to {a.clinician_name || "another clinician"} — read-only.
                </div>
              ) : canManage ? (
                <div className="space-y-3">
                  {/* Optional patient-visible note */}
                  <div>
                    <div className="fl-label mb-1">Optional note (visible to the patient)</div>
                    <textarea className="fl-input min-h-[56px]" rows={2} value={note}
                              onChange={(e) => setNote(e.target.value)}
                              placeholder="e.g. Please arrive 5 min early…" data-testid="cal-detail-note" />
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {isPending && (
                      <>
                        <button onClick={() => patch("confirmed")} disabled={saving} className="fl-btn text-xs flex-1 justify-center" data-testid="cal-detail-confirm">
                          <ShieldCheck size={12} /> Confirm
                        </button>
                        <button onClick={() => patch("declined")} disabled={saving} className="fl-btn-ghost text-xs flex-1 justify-center"
                                style={{ borderColor: "rgba(239,68,68,.5)", color: "#EF4444" }} data-testid="cal-detail-decline">
                          <ShieldAlert size={12} /> Decline
                        </button>
                      </>
                    )}
                    <button onClick={openReschedule} disabled={saving} className="fl-btn-ghost text-xs flex-1 justify-center" data-testid="cal-detail-reschedule-open">
                      <CalIcon size={12} /> Reschedule
                    </button>
                    <button onClick={messagePatient} className="fl-btn-ghost text-xs flex-1 justify-center" data-testid="cal-detail-message">
                      <MessageSquare size={12} /> Message
                    </button>
                  </div>

                  {/* Inline reschedule */}
                  {showReschedule && (
                    <div className="fl-card p-3" data-testid="cal-detail-reschedule-panel">
                      <div className="fl-label mb-2">Pick a free time</div>
                      <input type="date" className="fl-input mb-2" value={rDate}
                             onChange={(e) => { setRDate(e.target.value); loadSlots(e.target.value); }}
                             data-testid="cal-detail-reschedule-date" />
                      {slotsLoading ? (
                        <div className="text-xs text-zinc-500">Loading slots…</div>
                      ) : slots.length === 0 ? (
                        <div className="text-xs text-zinc-500">No free slots that day (off-day or fully booked).</div>
                      ) : (
                        <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto">
                          {slots.map((s) => (
                            <button key={s.iso} disabled={!s.available}
                                    onClick={() => setPicked(s.iso)}
                                    className={`text-xs py-1.5 rounded-md border transition-colors ${picked === s.iso ? "border-[#f47a20] text-[#f47a20]" : s.available ? "border-white/10 text-zinc-300 hover:border-white/30" : "border-white/5 text-zinc-600 opacity-40 cursor-not-allowed line-through"}`}
                                    data-testid={`cal-detail-slot-${s.time}`}>
                              {s.time}
                            </button>
                          ))}
                        </div>
                      )}
                      <button onClick={() => picked ? patch("rescheduled", { scheduled_at: picked }) : toast.error("Pick a free slot")}
                              disabled={saving || !picked} className="fl-btn text-xs mt-2 w-full justify-center" data-testid="cal-detail-reschedule-submit">
                        <CalIcon size={12} /> Confirm new time
                      </button>
                    </div>
                  )}

                  {/* Inline cancel — confirmed/rescheduled only */}
                  {!isPending && (
                    !confirmCancel ? (
                      <button onClick={() => { setConfirmCancel(true); setShowReschedule(false); }} className="fl-btn-ghost text-xs w-full justify-center"
                              style={{ borderColor: "rgba(239,68,68,.4)", color: "#EF4444" }} data-testid="cal-detail-cancel-open">
                        <Ban size={12} /> Cancel appointment
                      </button>
                    ) : (
                      <div className="fl-card p-3" style={{ borderColor: "rgba(239,68,68,.4)" }} data-testid="cal-detail-cancel-confirm">
                        <div className="text-sm mb-2">Cancel this appointment?</div>
                        <div className="flex gap-2">
                          <button onClick={() => patch("cancelled")} disabled={saving} className="fl-btn text-xs flex-1 justify-center"
                                  style={{ background: "rgba(239,68,68,.15)", color: "#EF4444", border: "1px solid rgba(239,68,68,.5)" }}
                                  data-testid="cal-detail-cancel-submit">Yes, cancel</button>
                          <button onClick={() => setConfirmCancel(false)} className="fl-btn-ghost text-xs flex-1 justify-center">Keep it</button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
                    This appointment is {st.label.toLowerCase()} — no further scheduling actions.
                  </div>
                  {a.patient_id && (
                    <button onClick={messagePatient} className="fl-btn-ghost text-xs w-full justify-center" data-testid="cal-detail-message">
                      <MessageSquare size={12} /> Message patient
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="fl-card p-3">
                <div className="fl-label mb-1">When (London)</div>
                <div className="text-sm">{londonDateKey(a.start)}</div>
                <div className="text-sm text-[#f47a20]">{detail.hm.str}{detail.endStr ? `–${detail.endStr}` : ""}</div>
              </div>
              <div className="flex items-start gap-1.5 text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
                <Shield size={12} className="text-emerald-400 shrink-0 mt-0.5" />
                {isOrg
                  ? "A busy block from the Functional LAB organisation calendar. It does not create a patient appointment and is read-only here."
                  : "This is a busy block from your personal Google Calendar. Titles and details stay private and can't be edited here."}
              </div>
              <button onClick={close} className="fl-btn-ghost w-full justify-center">Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function CreateModal({ slot, patients, close, onCreated, clinicianId }) {
  const mRef = useModalA11y({ open: true, onClose: close });
  const [patientId, setPatientId] = useState("");
  const [service, setService] = useState(SERVICES[0]);
  const [mode, setMode] = useState("in_person");
  const [duration, setDuration] = useState(60);
  const [time, setTime] = useState(`${String(slot.hour).padStart(2, "0")}:00`);
  const [saving, setSaving] = useState(false);

  const timeOptions = useMemo(() => {
    const out = [];
    for (let h = 7; h <= 20; h++) for (const m of [0, 30]) out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    return out;
  }, []);

  const submit = async () => {
    if (!patientId) return toast.error("Choose a patient");
    const [h, m] = time.split(":").map(Number);
    const scheduled_at = londonWallToISO(slot.dateKey, h, m);
    setSaving(true);
    try {
      await api.post("/appointments", {
        patient_id: patientId, clinician_id: clinicianId, service, mode,
        scheduled_at, duration_min: duration,
      });
      toast.success("Appointment created");
      await onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create appointment");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-auto" onClick={close}>
      <div ref={mRef} role="dialog" aria-modal="true" className="fl-card fl-card-elevated p-6 w-full max-w-md"
           onClick={(e) => e.stopPropagation()} data-testid="cal-create-modal">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="fl-label text-[#f47a20]">New appointment</div>
            <div className="fl-heading text-2xl mt-1">{slot.dateKey}</div>
            <div className="text-xs text-zinc-500 mt-1">London time</div>
          </div>
          <button onClick={close} aria-label="Close" data-testid="cal-create-close"><X size={16} className="text-zinc-400" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <div className="fl-label mb-1">Patient</div>
            <select className="fl-input" value={patientId} onChange={(e) => setPatientId(e.target.value)} data-testid="cal-create-patient" required>
              <option value="" disabled>Choose a patient</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <div className="fl-label mb-1">Service</div>
            <select className="fl-input" value={service} onChange={(e) => setService(e.target.value)} data-testid="cal-create-service">
              {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="fl-label mb-1">Start time</div>
              <select className="fl-input" value={time} onChange={(e) => setTime(e.target.value)} data-testid="cal-create-time">
                {timeOptions.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
              </select>
            </div>
            <div>
              <div className="fl-label mb-1">Duration</div>
              <select className="fl-input" value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="cal-create-duration">
                {DURATIONS.map((d) => <option key={d} value={d}>{d} min</option>)}
              </select>
            </div>
          </div>
          <div>
            <div className="fl-label mb-1">Mode</div>
            <div className="flex gap-2">
              {["in_person", "remote"].map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)}
                        className={`flex-1 px-4 py-2 rounded-md border text-sm ${mode === m ? "border-[#f47a20] text-[#f47a20]" : "border-white/10 text-zinc-400"}`}
                        data-testid={`cal-create-mode-${m}`}>
                  {m === "in_person" ? "In person" : "Remote"}
                </button>
              ))}
            </div>
          </div>
          <button onClick={submit} disabled={saving || !patientId} className="fl-btn w-full justify-center" data-testid="cal-create-submit">
            <CalendarPlus size={14} /> {saving ? "Creating…" : "Create appointment"}
          </button>
        </div>
      </div>
    </div>
  );
}
